import { useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { CalendarDays, CalendarPlus, List, Plus, Search, Sparkles } from 'lucide-react'
import { DEFAULT_COURSE, MEAL_SLOT_LABELS, normaliseName, type Course, type MealSlot } from '@shared/events'
import { PLATE_BUCKET_LABELS, planBySlot, plannedDishes, recipesById, type PlateBucket } from '@shared/plate'
import { addDays, startOfWeek, weekDays, weekdayLabel } from '@shared/time'
import { Chip, type Eleccion } from '../components/ElegirReceta'
import { GenerarSemana } from '../components/GenerarSemana'
import { PonerPlato } from '../components/PonerPlato'
import { Cabecera, MostrarExtras, Vacio } from '../components/ui'
import { VistaSemana } from '../components/VistaSemana'
import { useEvents, useToday } from '../lib/hooks'
import { COLOR_PORCION, cursosDeReceta, porcionesDeReceta } from '../lib/platos'
import { useMostrarExtras } from '../lib/preferencias'

const PORCIONES: PlateBucket[] = ['vegetal', 'proteina', 'cereal']

type Vista = 'platos' | 'semana'

const VISTAS = [
  { id: 'platos', label: 'Platos', Icono: List },
  { id: 'semana', label: 'Semana', Icono: CalendarDays },
] as const

interface Poniendo {
  eleccion: Eleccion
  curso: Course
  encaja: readonly MealSlot[]
}

/**
 * Planificación: todos los platos del hogar en una lista. Se toca uno y se
 * elige en qué día y en qué comida de la semana va, en vez de recorrer la
 * semana hueco a hueco. Empieza en la semana que viene, que es la que se
 * planifica; la vigente queda a un toque para revisarla.
 *
 * La otra vista es la semana de siempre, día a día, para ver cómo queda y
 * cambiar o quitar platos en su sitio.
 */
export function Platos() {
  const events = useEvents()
  const today = useToday()
  const { franjas } = useMostrarExtras()
  const [vista, setVista] = useState<Vista>('platos')
  const [offset, setOffset] = useState(1)
  const [busqueda, setBusqueda] = useState('')
  const [filtro, setFiltro] = useState<MealSlot | null>(null)
  const [poniendo, setPoniendo] = useState<Poniendo | null>(null)
  const [generando, setGenerando] = useState(false)

  const weekKey = useMemo(() => addDays(startOfWeek(today), offset * 7), [today, offset])
  const dias = useMemo(() => weekDays(weekKey), [weekKey])
  const plan = useMemo(() => planBySlot(events), [events])

  /** Dónde está ya cada receta esta semana, para verlo sin abrirla. */
  const sitios = useMemo(() => {
    const porReceta = new Map<string, string[]>()
    for (const dayKey of dias) {
      for (const slot of franjas) {
        for (const dish of plannedDishes(plan, dayKey, slot)) {
          if (!dish.recipeId) continue
          const lista = porReceta.get(dish.recipeId) ?? []
          lista.push(`${weekdayLabel(dayKey).slice(0, 3)} ${MEAL_SLOT_LABELS[slot].toLowerCase()}`)
          porReceta.set(dish.recipeId, lista)
        }
      }
    }
    return porReceta
  }, [plan, dias, franjas])

  const recetas = useMemo(
    () =>
      [...recipesById(events)]
        .map(([id, recipe]) => ({ id, recipe, porciones: porcionesDeReceta(recipe) }))
        .sort((a, b) => a.recipe.name.localeCompare(b.recipe.name, 'es')),
    [events],
  )
  const needle = normaliseName(busqueda)
  const filtradas = recetas.filter(
    ({ recipe }) =>
      (!needle || normaliseName(recipe.name).includes(needle)) &&
      (!filtro || recipe.slots.length === 0 || recipe.slots.includes(filtro)),
  )
  const texto = busqueda.trim()
  const puestos = [...sitios.values()].reduce((total, lista) => total + lista.length, 0)

  return (
    <>
      <Cabecera
        titulo="Planificación"
        subtitulo={
          recetas.length === 0
            ? 'Toca un plato para ponerlo en la semana.'
            : `${puestos} ${puestos === 1 ? 'plato puesto' : 'platos puestos'} ${offset === 0 ? 'esta semana' : 'la que viene'}`
        }
      />

      <div
        className="mt-4 grid grid-cols-2 gap-1 rounded-full border border-border bg-card p-1"
        role="radiogroup"
        aria-label="Vista"
      >
        {VISTAS.map(({ id, label, Icono }) => (
          <button
            key={id}
            type="button"
            role="radio"
            aria-checked={vista === id}
            onClick={() => setVista(id)}
            className={`label-caps inline-flex min-h-10 items-center justify-center gap-1.5 rounded-full outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring ${
              vista === id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-primary'
            }`}
          >
            <Icono className="h-3.5 w-3.5" />
            {label}
          </button>
        ))}
      </div>

      <div className="mt-3 flex gap-2" role="radiogroup" aria-label="Qué semana">
        <Chip activo={offset === 0} onClick={() => setOffset(0)}>
          Esta semana
        </Chip>
        <Chip activo={offset === 1} onClick={() => setOffset(1)}>
          La que viene
        </Chip>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <MostrarExtras />
        <button
          type="button"
          onClick={() => setGenerando(true)}
          disabled={(dias[6] ?? weekKey) < today}
          className="label-caps inline-flex min-h-10 items-center gap-1.5 rounded-full bg-primary px-4 text-primary-foreground outline-none transition-[opacity,transform] hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring active:scale-95 disabled:opacity-40"
        >
          <Sparkles className="h-3.5 w-3.5" />
          Generar semana
        </button>
      </div>

      {vista === 'platos' ? (
        <>
          <div className="relative mt-4">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              className="campo pl-10"
              value={busqueda}
              onChange={(event) => setBusqueda(event.target.value)}
              placeholder="Buscar plato o escribir otra cosa"
              aria-label="Buscar plato o escribir otra cosa"
            />
          </div>

          <div className="-mx-4 mt-3 flex gap-2 overflow-x-auto px-4 pb-1" role="radiogroup" aria-label="Para qué comida">
            <Chip activo={filtro === null} onClick={() => setFiltro(null)}>
              Todos
            </Chip>
            {franjas.map((slot) => (
              <Chip key={slot} activo={filtro === slot} onClick={() => setFiltro(slot)}>
                {MEAL_SLOT_LABELS[slot]}
              </Chip>
            ))}
          </div>

          <ul className="mt-3 space-y-1.5">
            {texto && !recetas.some(({ recipe }) => normaliseName(recipe.name) === normaliseName(texto)) && (
              <li>
                <button
                  type="button"
                  onClick={() =>
                    setPoniendo({ eleccion: { recipeId: null, freeText: texto }, curso: DEFAULT_COURSE, encaja: [] })
                  }
                  className="flex min-h-14 w-full items-center gap-2 rounded-2xl border border-dashed border-primary/50 px-4 py-2.5 text-left text-sm font-medium text-primary outline-none transition-colors hover:bg-accent/70 focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Plus className="h-4 w-4 shrink-0" strokeWidth={2.4} />
                  Poner «{texto}» sin receta
                </button>
              </li>
            )}

            <AnimatePresence initial={false}>
              {filtradas.map(({ id, recipe, porciones }, index) => {
                const donde = sitios.get(id) ?? []
                return (
                  <motion.li
                    key={id}
                    layout
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2, delay: Math.min(index, 10) * 0.02 }}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setPoniendo({
                          eleccion: { recipeId: id, freeText: '' },
                          curso: cursosDeReceta(recipe)[0] ?? DEFAULT_COURSE,
                          encaja: recipe.slots,
                        })
                      }
                      className={`flex min-h-14 w-full items-center gap-3 rounded-2xl border px-4 py-2.5 text-left outline-none transition-colors hover:border-primary/40 focus-visible:ring-2 focus-visible:ring-ring ${
                        donde.length > 0 ? 'border-sage/40 bg-filled' : 'border-border/70 bg-card'
                      }`}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">{recipe.name}</span>
                        <span className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1" aria-hidden="true">
                            {PORCIONES.map((bucket) => (
                              <span
                                key={bucket}
                                title={PLATE_BUCKET_LABELS[bucket]}
                                className={`h-1.5 w-1.5 rounded-full ${
                                  porciones.has(bucket) ? COLOR_PORCION[bucket] : 'bg-surface'
                                }`}
                              />
                            ))}
                          </span>
                          <span className={`truncate ${donde.length > 0 ? 'font-medium text-sage-deep' : ''}`}>
                            {donde.length > 0
                              ? donde.join(' · ')
                              : recipe.slots.map((slot) => MEAL_SLOT_LABELS[slot].toLowerCase()).join(', ')}
                          </span>
                        </span>
                      </span>
                      <CalendarPlus className="h-4 w-4 shrink-0 text-muted-foreground" />
                    </button>
                  </motion.li>
                )
              })}
            </AnimatePresence>

            {filtradas.length === 0 && !texto && (
              <li>
                <Vacio>
                  {recetas.length === 0
                    ? 'No hay platos todavía. Créalos en Recetas, o escribe arriba lo que vayáis a comer.'
                    : 'Ningún plato encaja en esa comida.'}
                </Vacio>
              </li>
            )}
          </ul>
        </>
      ) : (
        <VistaSemana weekKey={weekKey} today={today} />
      )}

      <AnimatePresence>
        {poniendo && (
          <PonerPlato
            key="poner"
            eleccion={poniendo.eleccion}
            cursoInicial={poniendo.curso}
            encaja={poniendo.encaja}
            weekKeyInicial={weekKey}
            today={today}
            onCerrar={() => setPoniendo(null)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {generando && (
          <GenerarSemana key="generar" weekKey={weekKey} desde={today} onCerrar={() => setGenerando(false)} />
        )}
      </AnimatePresence>
    </>
  )
}
